/**
 * Use the product rail for favorites dashboard to showcase products that were favorited
 *
 * @module views/components/ProductRailHorizontalCompactFavorites
 * @memberof -Common
 */
import './ProductRailHorizontalCompactFavorites.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import ProductCardCompact from '@ulta/core/components/ProductCardCompact/ProductCardCompact';
import { Rail, Slide } from '@ulta/core/components/Rail/Rail';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { hasItems } from '@ulta/core/utils/array/array';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

/**
 * Represents a ProductRailHorizontalCompactFavorites component
 *
 * @method
 * @param {ProductRailHorizontalCompactFavoritesProps} props - React properties passed from composition
 * @returns ProductRailHorizontalCompactFavorites
 */
export const ProductRailHorizontalCompactFavorites = function( props ){
  const {
    title,
    countLabel,
    items,
    _railADAmeta,
    _railMeta,
    alignment,
    previousClickAction,
    nextClickAction,
    viewAllAction
  } = props;

  const railRef = useRef( null );

  const sliderPerViewXL = 3;
  const slidesPerGroupXL = 2;
  const sliderPerViewLG = 4;
  const slidesPerGroupLG = 4;
  const sliderPerViewMD = 4;
  const slidesPerGroupMD = 4;
  const sliderPerViewSM = 2.4;
  const slidesPerGroupSM = 1;

  useIntersectionObserver( railRef, {
    root: props.root,
    rootMargin: props.rootMargin,
    threshold: props.threshold
  }, handleIntersection( props ) );

  if( !hasItems( items ) ){
    return null;
  }

  return (
    <div className='ProductRailHorizontalCompactFavorites'>
      <Rail
        railRef={ railRef }
        title={ title }
        items={ items }
        headline={ title }
        subheadline={ countLabel }
        viewAllAction={ viewAllAction }
        _railADAmeta={ _railADAmeta }
        _railMeta={ _railMeta }
        alignment={ alignment }
        previousClickAction={ previousClickAction }
        nextClickAction={ nextClickAction }
        sliderPerViewXL={ sliderPerViewXL }
        slidesPerGroupXL={ slidesPerGroupXL }
        sliderPerViewLG={ sliderPerViewLG }
        slidesPerGroupLG={ slidesPerGroupLG }
        sliderPerViewMD={ sliderPerViewMD }
        slidesPerGroupMD={ slidesPerGroupMD }
        slidesPerGroupSM={ slidesPerGroupSM }
        sliderPerViewSM={ sliderPerViewSM }
      >
        { items?.length > 0 && items?.map( ( item, cardsIndex ) => (
          <Slide key={ `ProductCardCompact-${ cardsIndex }` }>
            <ProductCardCompact
              { ...spreadProps( { ...item } ) }
            />
          </Slide>
        ) ) }
      </Rail>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {ProductRailHorizontalCompactFavoritesProps}
 * @property {object} INTERSECTION_OBSERVER_OPTIONS - The default initial values for intersection observer.
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  alignment: 'bottom'
};
/**
 * Property type definitions
 *
 * @typedef ProductRailHorizontalCompactFavoritesProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {object} viewAllAction - sets the url for action label.
 * @property {array} items - Set the Item array.
 * @property {string} alignment - sets the alignment for the text.
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} _railADAmeta - sets carouselAccessibility
 * @property {object} _railMeta - sets nextAccessibility, previousAccessibility
 */

export const propTypes =  {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  viewAllAction: PropTypes.object,
  items: PropTypes.array,
  alignment: PropTypes.string,
  _railADAmeta: PropTypes.object,
  _railMeta: PropTypes.object,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object
};

ProductRailHorizontalCompactFavorites.propTypes = propTypes;
ProductRailHorizontalCompactFavorites.defaultProps = defaultProps;

export default ProductRailHorizontalCompactFavorites;